import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GalleryPhoto {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
}

// ─── Full-width Carousel ──────────────────────────────────────────────────────

function FullWidthCarousel({ photos }: { photos: GalleryPhoto[] }) {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = photos.length;

  const goTo = useCallback(
    (idx: number) => {
      if (transitioning || count <= 1) return;
      setTransitioning(true);
      setCurrent(idx);
      setTimeout(() => setTransitioning(false), 600);
    },
    [transitioning, count],
  );

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  if (!count) return null;

  return (
    <div className="relative w-full h-full overflow-hidden">
      {photos.map((photo, i) => (
        <VibesitesImage
          key={i}
          url={photo.image ?? photo.original_url ?? ''}
          alt={photo.alt_text ?? photo.title ?? ''}
          priority={i === 0}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[600ms] ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {count > 1 && (
        <>
          <button
            onClick={prev}
            aria-label="Previous photo"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white border border-white/30 backdrop-blur-sm transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>

          <button
            onClick={next}
            aria-label="Next photo"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white border border-white/30 backdrop-blur-sm transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>

          <div className="absolute bottom-4 right-5 z-10 px-3 py-1 rounded-full bg-white/70 text-black text-sm font-medium tabular-nums select-none">
            {current + 1}/{count}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function HomeGalleryCarousel() {
  const galleryImages = useCollectionBinding<GalleryPhoto>('homegallerycarousel.gallery_images', {
    collection: 'photo_gallery',
    defaultLimit: 12,
  });

  const carouselPhotos = useMemo(
    () => galleryImages.filter((p) => p.image ?? p.original_url),
    [galleryImages],
  );

  if (carouselPhotos.length === 0) return null;

  return (
    <div className="bg-white">
      <div className="w-full h-[55vh] md:h-[68vh] min-h-[360px] max-h-[720px]">
        <FullWidthCarousel photos={carouselPhotos} />
      </div>
    </div>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeGalleryCarousel',
  displayName: 'Home Gallery Carousel',
  tokens: {},
  collectionBindings: {
    'homegallerycarousel.gallery_images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      defaultLimit: 12,
    },
  },
};

(HomeGalleryCarousel as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomeGalleryCarousel;
